import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';

const ACTIVE_LONG_FORM_CONTENT_TYPE = {
  generalText: 'general_text',
  image: 'image',
  webpage: 'web_page',
  blogPost: 'blog_post',
  email: 'email',
  whitePaper: 'white_paper',
  researchReport: 'research_report',
} as const;
type ActiveLongFormContentType =
  (typeof ACTIVE_LONG_FORM_CONTENT_TYPE)[keyof typeof ACTIVE_LONG_FORM_CONTENT_TYPE];

const DEPRECATED_LONG_FORM_CONTENT_TYPE = {
  instagramPost: 'instagram_caption',
  facebookPost: 'facebook_post',
  linkedinPost: 'linkedin_post',
  twitterPost: 'twitter_post',
  facebookAd: 'facebook_ad',
  linkedinAd: 'linkedin_ad',
  linkedinConversationAd: 'linkedin_conversation_ad',
  linkedinMessageAd: 'linkedin_message_ad',
  linkedinLeadGenFormAd: 'linkedin_lead_gen_form_ad',
  linkedinDocumentAd: 'linkedin_document_ad',
  linkedinTextAd: 'linkedin_text_ad',
  linkedinVideoAd: 'linkedin_video_ad',
  twitterAd: 'twitter_ad',
  googlePerformanceMaxAd: 'google_performance_max_ad',
  googleSearchAd: 'google_search_ad',
  pinterestAd: 'pinterest_ad',
} as const;
type DeprecatedLongFormContentType =
  (typeof DEPRECATED_LONG_FORM_CONTENT_TYPE)[keyof typeof DEPRECATED_LONG_FORM_CONTENT_TYPE];

export const LONG_FORM_CONTENT_TYPE = {
  ...ACTIVE_LONG_FORM_CONTENT_TYPE,
  ...DEPRECATED_LONG_FORM_CONTENT_TYPE,
} as const;
export type LongFormContentType = ActiveLongFormContentType | DeprecatedLongFormContentType;

export const getActiveLongFormContentTypes = (): LongFormContentType[] => {
  return Object.values(ACTIVE_LONG_FORM_CONTENT_TYPE);
};

interface LongFormContentTypeProps {
  title: string;
  generationTool?: GenerationTool;
}

const LONG_FORM_CONTENT_TYPE_PROPS: Record<LongFormContentType, LongFormContentTypeProps> = {
  [ACTIVE_LONG_FORM_CONTENT_TYPE.generalText]: {
    title: 'Text',
    generationTool: GENERATION_TOOL.basicContentImprover,
  },
  [ACTIVE_LONG_FORM_CONTENT_TYPE.image]: {
    title: 'Image',
    generationTool: GENERATION_TOOL.imageByStyle,
  },
  [ACTIVE_LONG_FORM_CONTENT_TYPE.webpage]: {
    title: 'Webpage',
    generationTool: GENERATION_TOOL.basicLandingPage, // TODO: use some non-deprecated tool
  },
  [ACTIVE_LONG_FORM_CONTENT_TYPE.blogPost]: {
    title: 'Blog / Article',
    generationTool: GENERATION_TOOL.blogDescription,
  },
  [ACTIVE_LONG_FORM_CONTENT_TYPE.email]: {
    title: 'Email',
    generationTool: GENERATION_TOOL.emailMixture,
  },
  [ACTIVE_LONG_FORM_CONTENT_TYPE.whitePaper]: {
    title: 'White paper',
    generationTool: GENERATION_TOOL.basicPerfectHeadline,
  },
  [ACTIVE_LONG_FORM_CONTENT_TYPE.researchReport]: {
    title: 'Research report',
    generationTool: GENERATION_TOOL.basicPerfectHeadline,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.instagramPost]: {
    title: 'Instagram Post',
    generationTool: GENERATION_TOOL.instagramCaption,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.facebookPost]: {
    title: 'Facebook Post',
    generationTool: GENERATION_TOOL.facebookPost,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.linkedinPost]: {
    title: 'LinkedIn Post',
    generationTool: GENERATION_TOOL.linkedinPost,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.twitterPost]: {
    title: 'X Post',
    generationTool: GENERATION_TOOL.tweet,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.facebookAd]: {
    title: 'Meta Image ad',
    generationTool: GENERATION_TOOL.facebookMixtureAd,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.linkedinAd]: {
    title: 'LinkedIn Single Image ad',
    generationTool: GENERATION_TOOL.linkedinMixtureAd,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.linkedinConversationAd]: {
    title: 'LinkedIn Conversation ad',
    generationTool: GENERATION_TOOL.linkedinConversationAd,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.linkedinMessageAd]: {
    title: 'LinkedIn Message ad',
    generationTool: GENERATION_TOOL.linkedinMessageAd,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.linkedinLeadGenFormAd]: {
    title: 'LinkedIn Lead Gen Form ad',
    generationTool: GENERATION_TOOL.linkedinLeadGenFormAd,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.linkedinDocumentAd]: {
    title: 'LinkedIn Document ad',
    generationTool: GENERATION_TOOL.linkedinDocumentAd,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.linkedinTextAd]: {
    title: 'LinkedIn Text ad',
    generationTool: GENERATION_TOOL.linkedinTextAd,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.linkedinVideoAd]: {
    title: 'LinkedIn Video ad',
    generationTool: GENERATION_TOOL.linkedinVideoAd,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.twitterAd]: {
    title: 'X Image ad',
    generationTool: GENERATION_TOOL.twitterMixtureAd,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.googlePerformanceMaxAd]: {
    title: 'Google Performance Max ad',
    generationTool: GENERATION_TOOL.googlePerformanceMaxAd,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.googleSearchAd]: {
    title: 'Google Search ad',
    generationTool: GENERATION_TOOL.googleRSA,
  },
  [DEPRECATED_LONG_FORM_CONTENT_TYPE.pinterestAd]: {
    title: 'Pinterest Image ad',
    generationTool: GENERATION_TOOL.pinterestMixtureAd,
  },
};

export const getLongFormContentTypeTitle = (contentType: LongFormContentType): string => {
  return LONG_FORM_CONTENT_TYPE_PROPS[contentType]?.title || contentType;
};

export const getLongFormContentTypeTool = (
  contentType: LongFormContentType
): GenerationTool | undefined => {
  return LONG_FORM_CONTENT_TYPE_PROPS[contentType]?.generationTool;
};
